export const categories = [
  "All Posts",
  "Cybersecurity",
  "Networking",
  "CCTV Systems",
  "Software Development",
  "Web Design",
  "IT Infrastructure",
];

const localPosts = [
  {
    slug: "vlan-segmentation-enterprise-security",
    title: "How VLAN Segmentation Improves Enterprise Security",
    category: "Networking",
    author: "Carium Tech Team",
    date: "March 15, 2026",
    readingTime: "5 min read",
    excerpt:
      "Learn how VLANs can isolate network traffic, reduce attack surfaces, and simplify compliance in multi-department enterprise environments.",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=450&fit=crop",
    content: {
      intro:
        "In today's interconnected enterprise environments, network security is no longer optional — it's a fundamental business requirement. VLANs (Virtual Local Area Networks) have emerged as a critical tool for segmenting traffic, improving performance, and hardening security postures without requiring additional hardware.",
      sections: [
        {
          heading: "What is VLAN Segmentation?",
          body: "VLAN segmentation divides a physical network into multiple logical networks. Devices within the same VLAN communicate as if they were on the same physical switch, while traffic between VLANs must pass through a router or Layer 3 switch — allowing you to enforce security policies at every boundary.",
        },
        {
          heading: "Key Security Benefits",
          body: "By segmenting sensitive systems (finance, HR, servers) into dedicated VLANs, you prevent lateral movement in case of a breach. If an attacker compromises a workstation in the guest VLAN, they cannot reach the database servers without traversing a firewall. This containment is the single most effective network security control you can implement.",
        },
        {
          heading: "Implementation Best Practices",
          body: "Start by grouping devices by function and sensitivity. Use private VLANs for isolated segments like guest Wi-Fi. Combine VLANs with 802.1X port authentication so devices are automatically placed in the correct VLAN based on identity. Always log inter-VLAN traffic for auditing and threat detection.",
        },
      ],
    },
  },
  {
    slug: "modern-cctv-systems-smart-businesses",
    title: "Modern CCTV Systems for Smart Businesses",
    category: "CCTV Systems",
    author: "Carium Tech Team",
    date: "March 10, 2026",
    readingTime: "6 min read",
    excerpt:
      "From IP cameras to AI-powered analytics — explore how modern CCTV systems are transforming business security and operations.",
    image:
      "https://images.unsplash.com/photo-1558002038-1055907df827?w=800&h=450&fit=crop",
    content: {
      intro:
        "Gone are the days of grainy analog footage and tapes. Modern CCTV systems leverage IP cameras, network video recorders, and artificial intelligence to deliver real-time insights alongside traditional security monitoring.",
      sections: [
        {
          heading: "IP Cameras vs Analog",
          body: "IP cameras offer significantly higher resolution (4MP to 12MP), Power over Ethernet for single-cable installation, and onboard processing for motion detection and analytics. Unlike analog systems, IP cameras can be accessed remotely and integrated with access control systems.",
        },
        {
          heading: "Remote Monitoring & Mobile Access",
          body: "Modern NVRs provide secure remote access via SSL VPN or cloud relay. Security personnel can view live feeds, playback recordings, and receive push alerts on their smartphones — enabling 24/7 vigilance without requiring on-site guards.",
        },
        {
          heading: "AI and Video Analytics",
          body: "AI-powered cameras can distinguish between humans, vehicles, and animals — reducing false alarms. Advanced analytics include license plate recognition, people counting, heat mapping, and behavior analysis. These features turn security systems into business intelligence tools.",
        },
      ],
    },
  },
  {
    slug: "why-businesses-need-network-monitoring",
    title: "Why Every Business Needs Network Monitoring",
    category: "IT Infrastructure",
    author: "Carium Tech Team",
    date: "March 5, 2026",
    readingTime: "5 min read",
    excerpt:
      "Proactive network monitoring prevents outages, identifies bottlenecks, and saves money. Here's why your business can't afford to skip it.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=450&fit=crop",
    content: {
      intro:
        "Most businesses only think about their network when something breaks. But reactive network management is costly — every minute of unplanned downtime can translate to thousands in lost revenue and productivity.",
      sections: [
        {
          heading: "The Cost of Downtime",
          body: "Studies show that the average cost of network downtime is $5,600 per minute for mid-size businesses. Network monitoring tools detect issues before they become outages — high latency, packet loss, bandwidth saturation, and hardware failures are all visible in real time.",
        },
        {
          heading: "Key Metrics to Monitor",
          body: "Track interface utilization, CPU and memory on switches/routers, latency between sites, packet loss percentages, and SNMP trap events. A good monitoring stack (like Zabbix, PRTG, or Grafana + Prometheus) provides dashboards, alerting, and historical trends.",
        },
        {
          heading: "Setting Up Alerts",
          body: "Configure thresholds for critical metrics and route alerts to Slack, email, or SMS. The goal is to be notified of abnormal conditions before users notice. A well-tuned monitoring system reduces mean time to resolution (MTTR) by up to 70%.",
        },
      ],
    },
  },
  {
    slug: "react-vs-nextjs-business-websites",
    title: "React vs Next.js for Modern Business Websites",
    category: "Software Development",
    author: "Carium Tech Team",
    date: "February 28, 2026",
    readingTime: "6 min read",
    excerpt:
      "Both are powerful. But which one is right for your next corporate website, web app, or client portal? We break down the key differences.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=450&fit=crop",
    content: {
      intro:
        "React and Next.js are two of the most popular frameworks for building modern web applications. While React is a library for building user interfaces, Next.js is a full framework built on top of React that adds server-side rendering, static site generation, and routing.",
      sections: [
        {
          heading: "When to Choose React",
          body: "React (with Create React App or Vite) is ideal for single-page applications, internal tools, and dashboards where SEO is not a primary concern. It's simpler to deploy and has a huge ecosystem of libraries. If you're building a client-facing portal behind a login, React is often the right choice.",
        },
        {
          heading: "When to Choose Next.js",
          body: "Next.js excels at SEO-critical public websites, e-commerce platforms, and marketing sites. Server-side rendering ensures search engines index your content correctly. Next.js also offers API routes, image optimization, and incremental static regeneration out of the box — features that significantly reduce development time.",
        },
        {
          heading: "Our Recommendation",
          body: "For most business websites, we recommend Next.js. The SEO benefits alone justify the migration. However, for highly interactive web applications with complex state management, a well-structured React app with client-side routing remains a solid choice.",
        },
      ],
    },
  },
  {
    slug: "how-firewalls-protect-corporate-infrastructure",
    title: "How Firewalls Protect Corporate Infrastructure",
    category: "Cybersecurity",
    author: "Carium Tech Team",
    date: "February 20, 2026",
    readingTime: "5 min read",
    excerpt:
      "Firewalls are the first line of defense. Understand the different types — packet filtering, stateful inspection, and next-generation — and how they work together.",
    image:
      "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800&h=450&fit=crop",
    content: {
      intro:
        "A firewall is a network security device that monitors and controls incoming and outgoing traffic based on predetermined security rules. Think of it as the security guard at the entrance of your corporate network — it decides who gets in and what leaves.",
      sections: [
        {
          heading: "Types of Firewalls",
          body: "Packet-filtering firewalls inspect packets at the network layer. Stateful firewalls track connection states to make smarter decisions. Next-generation firewalls (NGFWs) add application awareness, intrusion prevention, and SSL inspection. Most enterprises use a combination of all three.",
        },
        {
          heading: "Where to Deploy",
          body: "Perimeter firewalls sit at the network edge between your LAN and the internet. Internal firewalls segment sensitive zones like data centers and finance departments. Cloud firewalls protect virtual networks in AWS, Azure, or Google Cloud. Each layer adds depth to your defense.",
        },
        {
          heading: "Common Configuration Mistakes",
          body: "Overly permissive rules (allowing 'any any'), unused open ports, and lack of logging are the most common firewall misconfigurations. Regularly audit your rulebase, remove stale rules, and enable logging for critical traffic flows.",
        },
      ],
    },
  },
  {
    slug: "building-secure-isp-infrastructure-2026",
    title: "Building Secure ISP Infrastructure in 2026",
    category: "IT Infrastructure",
    author: "Carium Tech Team",
    date: "February 14, 2026",
    readingTime: "7 min read",
    excerpt:
      "The demands on ISP infrastructure have never been higher. Learn how to design a resilient, scalable, and secure ISP network for modern subscribers.",
    image:
      "https://images.unsplash.com/photo-1563770660941-10a45f3bdb5e?w=800&h=450&fit=crop",
    content: {
      intro:
        "Internet Service Providers face a unique challenge: building networks that are both highly available and secure, while serving thousands of subscribers with varying usage patterns. The architecture decisions made today will impact service quality for years to come.",
      sections: [
        {
          heading: "Core Architecture Principles",
          body: "Design with redundancy at every layer — power, links, and devices. Use OSPF or IS-IS for internal routing and BGP for upstream peering. Implement MPLS if you plan to offer VPN services. Always separate management, subscriber, and backbone traffic using VRFs or VLANs.",
        },
        {
          heading: "Security at the Edge",
          body: "Deploy RTBH (Remotely Triggered Black Hole) filtering to mitigate DDoS attacks. Use uRPF (unicast Reverse Path Forwarding) to prevent IP spoofing. ACLs at the edge should block RFC 1918 addresses and known malicious IP ranges. Monitor BGP announcements with services like BGPmon.",
        },
        {
          heading: "Scaling for Growth",
          body: "Plan for 3x your initial subscriber count. Use modular chassis switches that allow line card upgrades without replacing the entire chassis. Implement zero-touch provisioning (ZTP) so new CPE devices configure themselves automatically when connected.",
        },
      ],
    },
  },
  {
    slug: "top-cybersecurity-risks-facing-smes",
    title: "Top Cybersecurity Risks Facing SMEs in 2026",
    category: "Cybersecurity",
    author: "Carium Tech Team",
    date: "February 8, 2026",
    readingTime: "6 min read",
    excerpt:
      "Small and medium enterprises are prime targets for cyber attacks. Discover the most pressing threats and how to defend against them on a budget.",
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=450&fit=crop",
    content: {
      intro:
        "Contrary to popular belief, small and medium enterprises (SMEs) are more likely to be targeted by cyber attacks than large corporations. Why? Because they often have weaker security postures but still hold valuable data — customer information, financial records, and intellectual property.",
      sections: [
        {
          heading: "1. Ransomware",
          body: "Ransomware remains the top threat for SMEs. Attackers encrypt your files and demand payment for the decryption key. Prevention includes regular off-site backups, email filtering, and user awareness training. Never pay the ransom — it funds further attacks and doesn't guarantee data recovery.",
        },
        {
          heading: "2. Phishing and Social Engineering",
          body: "Over 90% of data breaches start with a phishing email. Train employees to recognize suspicious emails, implement DMARC/DKIM/SPF email authentication, and use multi-factor authentication everywhere. A single compromised credential can lead to a full network takeover.",
        },
        {
          heading: "3. Unpatched Vulnerabilities",
          body: "Outdated software is a leading cause of breaches. Implement a patch management policy — critical security patches should be applied within 48 hours. Use vulnerability scanners (like OpenVAS or Nessus) to identify weaknesses before attackers do.",
        },
      ],
    },
  },
  {
    slug: "designing-scalable-office-networks",
    title: "Designing Scalable Office Networks for Growing Teams",
    category: "Networking",
    author: "Carium Tech Team",
    date: "February 1, 2026",
    readingTime: "6 min read",
    excerpt:
      "Future-proof your office network with a design that scales from 20 to 200+ users without requiring a complete redesign.",
    image:
      "https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800&h=450&fit=crop",
    content: {
      intro:
        "A well-designed office network should accommodate growth seamlessly. Whether you're expanding from 20 to 50 employees or opening a second floor, the right architecture prevents costly redesigns and downtime.",
      sections: [
        {
          heading: "The Three-Tier Model",
          body: "Enterprise networks traditionally use a three-tier design: access (user connectivity), distribution (policy enforcement), and core (high-speed backbone). For small offices, a collapsed-core design combining distribution and core into one switch is more practical and cost-effective.",
        },
        {
          heading: "Cabling and Infrastructure",
          body: "Install Cat6a or fiber cabling — the cost difference is minimal compared to the cost of re-cabling later. Run at least two drops per desk and extra runs to common areas. Use patch panels for a clean, manageable wiring closet. Label everything clearly.",
        },
        {
          heading: "Wi-Fi Design Considerations",
          body: "Conduct a site survey before installing access points. Avoid channel overlap by using DFS channels and 5 GHz bands where possible. For open-plan offices, one AP per 500 sq ft is a good starting point. Use a controller-based system for seamless roaming.",
        },
      ],
    },
  },
  {
    slug: "why-businesses-need-digital-transformation",
    title: "Why Businesses Need Digital Transformation in 2026",
    category: "Web Design",
    author: "Carium Tech Team",
    date: "January 25, 2026",
    readingTime: "5 min read",
    excerpt:
      "Digital transformation is more than a buzzword. It's a survival strategy. Learn how modernizing your technology stack drives growth and efficiency.",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=450&fit=crop",
    content: {
      intro:
        "Digital transformation means integrating digital technology into all areas of your business, fundamentally changing how you operate and deliver value to customers. It's not just about adopting new tools — it's about reimagining your entire business model for the digital age.",
      sections: [
        {
          heading: "Why Now?",
          body: "Customer expectations have shifted permanently. They expect seamless online experiences, instant responses, and personalized service. Businesses that fail to adapt risk becoming irrelevant. The pandemic accelerated digital adoption by 5-7 years — catching up is no longer optional.",
        },
        {
          heading: "Key Areas to Transform",
          body: "Start with customer-facing systems (website, portal, e-commerce), then move to internal operations (ERP, CRM, collaboration tools). Automate repetitive tasks, migrate to cloud infrastructure, and implement data analytics to drive decision-making. Each step builds on the last.",
        },
        {
          heading: "Measuring Success",
          body: "Define KPIs before you start: revenue growth, customer acquisition cost, employee productivity, and system uptime. Digital transformation is a journey, not a destination. Plan in 6-month sprints, measure results, and adjust. The businesses that transform continuously will thrive.",
        },
      ],
    },
  },
  {
    slug: "database-design-patterns-growing-applications",
    title: "Database Design Patterns for Growing Applications",
    category: "Software Development",
    author: "Carium Tech Team",
    date: "January 18, 2026",
    readingTime: "7 min read",
    excerpt:
      "A well-designed database is the backbone of any scalable application. Learn the patterns that keep your data layer performant as your user base grows.",
    image:
      "https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=800&h=450&fit=crop",
    content: {
      intro:
        "Every successful application eventually faces the same challenge: what worked for 100 users breaks at 100,000. Database design is often the bottleneck. The right patterns applied early can save months of painful refactoring later.",
      sections: [
        {
          heading: "Normalization vs Denormalization",
          body: "Normalizing your schema (3NF or BCNF) eliminates redundancy and ensures data integrity — ideal for write-heavy workloads. But as read queries multiply, denormalizing strategically (adding computed columns, materialized views, or summary tables) can reduce query latency by orders of magnitude. The key is knowing when to cross that line.",
        },
        {
          heading: "Indexing Strategies",
          body: "A well-placed index can turn a 5-second query into a 5-millisecond one. Composite indexes should follow the leftmost prefix rule. Partial indexes are powerful for queries that filter on a specific condition (like active users). But over-indexing slows writes — profile your actual query patterns before adding indexes.",
        },
        {
          heading: "Connection Pooling and Read Replicas",
          body: "At scale, connection overhead becomes a bottleneck. Use PgBouncer or built-in pooling to manage database connections efficiently. Read replicas distribute query load across multiple servers, giving you horizontal read scaling without rewriting application code.",
        },
      ],
    },
  },
  {
    slug: "gpon-fiber-deployment-practical-guide",
    title: "GPON Fiber Deployment: A Practical Guide for ISPs",
    category: "IT Infrastructure",
    author: "Carium Tech Team",
    date: "January 12, 2026",
    readingTime: "8 min read",
    excerpt:
      "Fiber-to-the-home is the gold standard for broadband delivery. Here's how to plan and deploy a GPON network that scales from 50 to 5,000 subscribers.",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=450&fit=crop",
    content: {
      intro:
        "Gigabit Passive Optical Network (GPON) technology delivers high-speed internet over fiber optic cables using a point-to-multipoint architecture. For ISPs in East Africa, GPON offers a cost-effective path to delivering 1 Gbps+ speeds to subscribers while keeping operational costs low.",
      sections: [
        {
          heading: "Network Architecture",
          body: "A GPON network consists of three layers: the Optical Line Terminal (OLT) at the central office, the passive optical splitter in the field, and the Optical Network Terminal (ONT) at the customer premises. A single fiber from the OLT can serve up to 128 ONTs through cascaded splitters, dramatically reducing per-subscriber infrastructure costs.",
        },
        {
          heading: "Fiber Plant Design",
          body: "Route planning is critical. Use existing utility poles where possible (aerial deployment) to reduce civil works costs. For underground routes, micro-trenching is 40-60% cheaper than traditional duct installation. Always plan for 30% more fiber capacity than your initial subscriber count — the marginal cost of extra strands is negligible compared to re-entering the field later.",
        },
        {
          heading: "Operations and Monitoring",
          body: "Deploy an OSS platform to monitor OLT health, ONT signal levels, and subscriber bandwidth utilization. GPON supports OMCI (ONU Management and Control Interface) for remote provisioning — new subscribers can be activated in minutes without a truck roll. Set up alerts for signal degradation to catch fiber bends or cuts before they cause outages.",
        },
      ],
    },
  },
  {
    slug: "python-network-automation-ansible",
    title: "Python Network Automation with Ansible",
    category: "Networking",
    author: "Carium Tech Team",
    date: "January 5, 2026",
    readingTime: "7 min read",
    excerpt:
      "Manual CLI configuration is error-prone and slow. Learn how Python and Ansible together can automate network device configuration, backups, and compliance checks.",
    image:
      "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&h=450&fit=crop",
    content: {
      intro:
        "Network engineers spend hours manually configuring switches, routers, and firewalls — work that's repetitive, error-prone, and doesn't scale. Python and Ansible change that by letting you define your network state in code and apply it consistently across hundreds of devices.",
      sections: [
        {
          heading: "Why Ansible for Networking",
          body: "Ansible uses SSH (not APIs) to configure devices, making it compatible with legacy equipment. Its agentless architecture means nothing needs to be installed on network devices. Playbooks are written in YAML — readable by both engineers and operators. Modules exist for Cisco IOS, Juniper, Arista, MikroTik, and more.",
        },
        {
          heading: "Python for Custom Automation",
          body: "When Ansible modules don't cover your use case, Python fills the gap. Netmiko handles SSH sessions to hundreds of device types. NAPALM provides a vendor-agnostic API for configuration and facts. Use Python to build custom tools: automated backup scripts, config diff reporters, or SNMP-based inventory collectors.",
        },
        {
          heading: "A Practical Workflow",
          body: "Start with configuration backups — automate daily backups of all devices to a Git repository. Then move to compliance checks: define your standard config (NTP, syslog, SNMP, SSH settings) and use Ansible to audit and remediate drift. Finally, tackle provisioning: template new device configs with Jinja2 and deploy them in a single playbook run.",
        },
      ],
    },
  },
  {
    slug: "technical-spatial-planning-concept-blueprint",
    title: "Technical Spatial Planning: From Concept to Blueprint",
    category: "Web Design",
    author: "Carium Tech Team",
    date: "December 28, 2025",
    readingTime: "6 min read",
    excerpt:
      "Whether it's a residential home, container cabin, or commercial space — learn how technical spatial planning turns ideas into precise, buildable drawings.",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&h=450&fit=crop",
    content: {
      intro:
        "Technical spatial planning bridges the gap between an architectural concept and a buildable reality. It's where creative vision meets engineering precision — ensuring every dimension, material, and structural element is specified before construction begins.",
      sections: [
        {
          heading: "The Planning Process",
          body: "Start with site analysis: plot dimensions, soil type, access points, utilities, and local building codes. Then move to concept design: room layouts, traffic flow, natural lighting, and ventilation. The technical phase translates this into dimensioned floor plans, elevations, sections, and structural details that a contractor can execute without ambiguity.",
        },
        {
          heading: "Container Cabin Design",
          body: "Container cabins require special attention to structural integrity. Cutting walls for windows and doors weakens the corrugated steel frame — reinforcement with steel headers and frames is essential. Insulation is critical: closed-cell spray foam provides the best R-value per inch while preventing condensation inside the metal shell.",
        },
        {
          heading: "Digital Tools and Deliverables",
          body: "Modern spatial planning uses CAD software (AutoCAD, SketchUp, or Revit) to produce precise drawings. Deliverables typically include: floor plans with dimensions, electrical layouts, plumbing diagrams, structural details, and a bill of materials. These documents ensure accurate quoting, reduce material waste, and prevent costly on-site changes.",
        },
      ],
    },
  },
  {
    slug: "protecting-business-ransomware-2026",
    title: "Protecting Your Business from Ransomware in 2026",
    category: "Cybersecurity",
    author: "Carium Tech Team",
    date: "December 20, 2025",
    readingTime: "6 min read",
    excerpt:
      "Ransomware attacks are evolving faster than ever. Here's a practical, budget-conscious defence strategy for businesses of all sizes.",
    image:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=450&fit=crop",
    content: {
      intro:
        "Ransomware has evolved from a nuisance to a existential business threat. In 2025, the average ransom demand exceeded $1.5 million, and the average recovery cost — even without paying — reached $4.5 million. But the good news: most attacks exploit known weaknesses that are straightforward to address.",
      sections: [
        {
          heading: "The 3-2-1 Backup Rule",
          body: "Keep 3 copies of your data, on 2 different media types, with 1 copy offsite (and offline). Test your backups monthly — an untested backup is not a backup. Modern ransomware specifically targets backup systems first, so ensure your offline copy is truly isolated from the network.",
        },
        {
          heading: "Email Security Gateways",
          body: "Since 90%+ of ransomware arrives via email, deploy a gateway that filters phishing links and malicious attachments before they reach inboxes. Solutions like Microsoft Defender for Office 365, Proofpoint, or Mimecast provide multi-layered protection. Enable DMARC/DKIM/SPF on your domain to prevent email spoofing.",
        },
        {
          heading: "Network Segmentation",
          body: "If ransomware enters one system, segmentation prevents it from spreading. Use VLANs and firewall rules to isolate critical systems (file servers, databases, backups) from general user networks. Implement zero-trust principles: verify every connection, regardless of whether it originates inside or outside the perimeter.",
        },
      ],
    },
  },
  {
    slug: "choosing-right-cctv-system-office",
    title: "How to Choose the Right CCTV System for Your Office",
    category: "CCTV Systems",
    author: "Carium Tech Team",
    date: "December 14, 2025",
    readingTime: "5 min read",
    excerpt:
      "With dozens of camera brands and technologies available, choosing the right CCTV system can be overwhelming. Here's a decision framework that simplifies the process.",
    image:
      "https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=800&h=450&fit=crop",
    content: {
      intro:
        "A CCTV system is a significant investment — not just in hardware, but in ongoing storage, maintenance, and monitoring. Choosing the right system the first time avoids costly replacements and ensures your security coverage matches your actual risk profile.",
      sections: [
        {
          heading: "Assess Your Coverage Needs",
          body: "Walk through your premises and identify key areas: entrances/exits, reception, parking, server rooms, cash handling areas, and perimeter boundaries. Each area has different requirements — parking lots need IR night vision and wide-angle lenses, while reception needs high resolution for facial identification.",
        },
        {
          heading: "Camera Selection Guide",
          body: "For indoor areas, 2-4MP bullet cameras with wide dynamic range (WDR) handle mixed lighting. Outdoor cameras need IP67 weatherproofing and IR illumination for night vision. Pan-tilt-zoom (PTZ) cameras are ideal for large open areas where a single camera can cover multiple angles. For high-security areas, 4K cameras provide maximum detail for forensic review.",
        },
        {
          heading: "Storage and Retention",
          body: "Calculate storage based on camera count, resolution, and retention period. A single 4MP camera recording at 15fps uses approximately 20GB per day. H.265+ compression reduces this by 50-70% compared to H.264. For 30-day retention with 16 cameras, plan for 5-10TB of storage. Use motion-triggered recording to extend retention significantly.",
        },
      ],
    },
  },
  {
    slug: "web-performance-core-web-vitals",
    title: "Web Performance Optimisation: Core Web Vitals",
    category: "Software Development",
    author: "Carium Tech Team",
    date: "December 7, 2025",
    readingTime: "6 min read",
    excerpt:
      "Google uses Core Web Vitals as a ranking factor. Learn how to measure and optimise LCP, INP, and CLS for better SEO and user experience.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=450&fit=crop",
    content: {
      intro:
        "Page speed directly impacts user satisfaction, conversion rates, and search engine rankings. Google's Core Web Vitals — LCP, INP, and CLS — measure the real-world experience of your visitors. Optimising these metrics isn't just good SEO practice; it's good business.",
      sections: [
        {
          heading: "Largest Contentful Paint (LCP)",
          body: "LCP measures how quickly the main content of your page loads. The target is under 2.5 seconds. Common culprits: unoptimised images, render-blocking JavaScript, slow server response times, and client-side rendering. Fix: use next-gen image formats (WebP/AVIF), preload critical resources, implement server-side rendering, and use a CDN.",
        },
        {
          heading: "Interaction to Next Paint (INP)",
          body: "INP measures responsiveness — how quickly the page responds to user interactions like clicks and keystrokes. The target is under 200ms. Slow INP usually stems from long JavaScript tasks blocking the main thread. Fix: break long tasks into smaller chunks, use web workers for heavy computation, and lazy-load non-critical scripts.",
        },
        {
          heading: "Cumulative Layout Shift (CLS)",
          body: "CLS measures visual stability — how much the page layout shifts during loading. The target is under 0.1. Common causes: images without dimensions, dynamically injected content, web fonts causing FOIT/FOUT, and ads/embeds without reserved space. Fix: always specify width/height on images, use font-display: swap, and reserve space for dynamic content.",
        },
      ],
    },
  },
  {
    slug: "wazuh-siem-small-business",
    title: "Setting Up Wazuh SIEM for Small Business Security",
    category: "Cybersecurity",
    author: "Carium Tech Team",
    date: "November 30, 2025",
    readingTime: "8 min read",
    excerpt:
      "Enterprise-grade security monitoring doesn't require an enterprise budget. Wazuh is free, open-source, and powerful enough to protect small business networks.",
    image:
      "https://images.unsplash.com/photo-1563986768609-322da13575f2?w=800&h=450&fit=crop",
    content: {
      intro:
        "Small businesses often assume SIEM (Security Information and Event Management) is only for large enterprises with dedicated SOC teams. Wazuh changes that equation. It's a free, open-source SIEM that provides log analysis, intrusion detection, vulnerability scanning, and compliance reporting — all from a single platform.",
      sections: [
        {
          heading: "What Wazuh Monitors",
          body: "Wazuh agents installed on endpoints collect system logs, file integrity data, and process information. The manager correlates events across all agents to detect: failed login attempts, privilege escalation, malware execution, unauthorized file changes, and policy violations. It also integrates with firewalls, routers, and cloud platforms to centralize visibility.",
        },
        {
          heading: "Deployment Architecture",
          body: "For small businesses (under 50 endpoints), a single Wazuh manager + indexer instance on a 4-core, 16GB RAM server handles the load comfortably. Install the manager on one server, the Wazuh Indexer (OpenSearch) on the same or separate server, and the dashboard for visualization. Deploy lightweight agents on each endpoint — they consume minimal resources.",
        },
        {
          heading: "Rules and Alerting",
          body: "Wazuh ships with thousands of out-of-the-box detection rules mapped to MITRE ATT&CK techniques. Customize rules to your environment: alert on PowerShell execution on non-admin workstations, detect lateral movement via RDP, or flag DNS requests to known malicious domains. Route critical alerts to email, Slack, or your ticketing system for immediate response.",
        },
      ],
    },
  },
  {
    slug: "building-resilient-multi-site-networks-ospf",
    title: "Building Resilient Multi-Site Networks with OSPF",
    category: "Networking",
    author: "Carium Tech Team",
    date: "November 22, 2025",
    readingTime: "7 min read",
    excerpt:
      "When your business spans multiple locations, OSPF provides the dynamic routing intelligence to keep traffic flowing efficiently — even when links fail.",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=450&fit=crop",
    content: {
      intro:
        "Multi-site networks present a unique challenge: how do you route traffic between locations when you don't control the underlying transport? OSPF (Open Shortest Path First) solves this by dynamically learning the best paths between sites and reconverging in seconds when a link goes down.",
      sections: [
        {
          heading: "OSPF Area Design",
          body: "Design your OSPF network with a backbone area (Area 0) connecting all sites. Each site gets its own area to limit the blast radius of topology changes. Area boundary routers (ABRs) summarize routes between areas, reducing the routing table size on internal routers. For networks with fewer than 10 sites, a single-area design is acceptable.",
        },
        {
          heading: "WAN Link Redundancy",
          body: "Connect sites with at least two independent WAN links — primary MPLS and backup broadband, for example. OSPF automatically detects link failures and reroutes traffic through the remaining path within seconds. Use interface cost tuning to prefer the primary link under normal conditions and only failover when necessary.",
        },
        {
          heading: "Troubleshooting OSPF",
          body: "The most common OSPF issues: mismatched hello/dead timers, MTU mismatches preventing adjacency, area type mismatches, and network type misconfigurations. Use 'show ip ospf neighbor' to verify adjacency state. Neighbors should reach FULL state (or 2WAY on broadcast networks). If stuck in INIT or EXSTART, check MTU and authentication settings.",
        },
      ],
    },
  },
  {
    slug: "container-cabin-technical-specifications",
    title: "Container Cabin Design: Technical Specifications",
    category: "Web Design",
    author: "Carium Tech Team",
    date: "November 15, 2025",
    readingTime: "6 min read",
    excerpt:
      "Shipping container cabins are an innovative, cost-effective building solution. Here are the technical specifications you need to know before building.",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=450&fit=crop",
    content: {
      intro:
        "Container cabins have moved from novelty to mainstream construction. A standard 20ft or 40ft shipping container can be transformed into a comfortable living or working space at a fraction of traditional construction costs. But success depends on understanding the structural and thermal challenges of working with corrugated steel.",
      sections: [
        {
          heading: "Structural Considerations",
          body: "A 40ft container is designed to carry 26-30 tonnes uniformly distributed across its floor. The corner posts are the primary load-bearing elements. When cutting openings for windows and doors, always install steel headers (minimum 50x50x3mm RHS) above openings to maintain structural integrity. Never cut corner posts or the top rail without engineer approval.",
        },
        {
          heading: "Insulation and Climate Control",
          body: "Bare steel conducts heat and cold efficiently — the opposite of what you want in a building. Use closed-cell spray foam (minimum 50mm walls, 75mm roof) to achieve R-values comparable to traditional construction. Spray foam also acts as a vapor barrier, preventing condensation inside the metal shell. For hot climates, add reflective roof coating to reduce solar heat gain by up to 40%.",
        },
        {
          heading: "Electrical and Plumbing",
          body: "Route electrical conduits through the insulation layer — never directly against the metal skin where condensation forms. Use surface-mounted trunking for easy access. For plumbing, PEX piping is ideal: flexible, frost-resistant, and easy to route through tight spaces. Install all plumbing on interior walls where possible to simplify drainage and reduce freeze risk.",
        },
      ],
    },
  },
  {
    slug: "devops-pipeline-ci-cd-github-actions",
    title: "DevOps Pipeline: CI/CD with GitHub Actions",
    category: "Software Development",
    author: "Carium Tech Team",
    date: "November 8, 2025",
    readingTime: "7 min read",
    excerpt:
      "Automate your build, test, and deployment workflow with GitHub Actions. From commit to production in minutes, not hours.",
    image:
      "https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=800&h=450&fit=crop",
    content: {
      intro:
        "Manual deployments are slow, error-prone, and stressful. A CI/CD pipeline automates the entire journey from code commit to production deployment — running tests, building artifacts, scanning for vulnerabilities, and deploying to your infrastructure. GitHub Actions makes this accessible to any team using GitHub.",
      sections: [
        {
          heading: "Pipeline Stages",
          body: "A typical CI/CD pipeline has four stages: Build (compile code, install dependencies), Test (run unit tests, integration tests, linting), Security (scan for vulnerabilities in dependencies and container images), and Deploy (push to staging, run smoke tests, deploy to production). Each stage gates the next — a test failure prevents deployment.",
        },
        {
          heading: "GitHub Actions Workflow",
          body: "Workflows are defined in YAML files under .github/workflows/. A basic Node.js pipeline: checkout code, setup Node.js, install dependencies, run tests, build, and deploy. GitHub provides free CI minutes for public repositories and 2,000 minutes/month for private repos on the free tier. Reusable actions from the marketplace handle common tasks like SSH deployment, Docker builds, and Slack notifications.",
        },
        {
          heading: "Best Practices",
          body: "Cache dependencies between runs to speed up builds. Use matrix strategies to test across multiple Node/Python/Java versions simultaneously. Implement branch protection rules so PRs must pass CI before merging. Store secrets (API keys, deploy credentials) in GitHub Secrets — never in workflow files. Use environment protection rules for production deployments with required approvals.",
        },
      ],
    },
  },
];

export { localPosts };

export function getAllPosts() {
  return localPosts;
}

export function getPostsByPage(page, perPage = 9) {
  const start = (page - 1) * perPage;
  const end = start + perPage;
  return {
    posts: localPosts.slice(start, end),
    total: localPosts.length,
    totalPages: Math.ceil(localPosts.length / perPage),
  };
}

export function getPostBySlug(slug) {
  return localPosts.find((p) => p.slug === slug) || null;
}

export function getRelatedPosts(currentSlug, count = 3) {
  const current = getPostBySlug(currentSlug);
  if (!current) return localPosts.slice(0, count);

  return localPosts
    .filter((p) => p.slug !== currentSlug)
    .sort((a, b) => {
      if (a.category === current.category && b.category !== current.category) return -1;
      if (a.category !== current.category && b.category === current.category) return 1;
      return 0;
    })
    .slice(0, count);
}

export function searchPosts(query) {
  const q = query.toLowerCase().trim();
  if (!q) return localPosts;

  return localPosts.filter(
    (p) =>
      p.title.toLowerCase().includes(q) ||
      p.excerpt.toLowerCase().includes(q) ||
      p.content.intro.toLowerCase().includes(q) ||
      p.content.sections.some(
        (s) =>
          s.heading.toLowerCase().includes(q) || s.body.toLowerCase().includes(q)
      )
  );
}

export function getPostsByCategory(category) {
  if (!category || category === "All Posts") return localPosts;
  return localPosts.filter((p) => p.category === category);
}
